import React, { useState, useEffect } from 'react';
import { FiNavigation, FiArrowLeft, FiRefreshCw, FiAlertCircle, FiClock, FiMapPin } from 'react-icons/fi';
import { FaTrain } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/Common/Card';
import Button from '../components/Common/Button';
import Shimmer from '../components/Common/Shimmer';
import apiService from '../services/api';
import styles from './LiveStationResultPage.module.css';
import { i } from 'framer-motion/dist/types.d-BJcRxCew';

interface TrainAtStation {
  trainNo: string;
  trainName: string;
  arrivalTime: string;
  departureTime: string;
  platform: string;
  delay: string;
  status: string;
  [key: string]: any;
}

const LiveStationResultPage: React.FC = () => {
  const { stationCode } = useParams<{ stationCode: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stationData, setStationData] = useState<any>(null);
  const [trains, setTrains] = useState<TrainAtStation[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (stationCode) {
      fetchStationData(stationCode);
    } else {
      navigate('/live-station');
    }
  }, [stationCode]);

  const fetchStationData = async (code: string) => {
    setIsLoading(true);
    setError(null);
    setStationData(null);
    setTrains([]);

    try {
      const response = await apiService.getLiveAtStation(code);

      if (response.success && response.data) {
        setStationData(response.data);
        
        // Parse the response and extract trains
        const parsedTrains = parseTrainsData(response.data);
        
        if (parsedTrains.length > 0) {
          setTrains(parsedTrains);
          toast.success(`Found ${parsedTrains.length} trains at station!`);
        } else {
          setError('No trains found at this station currently');
          toast.error('No trains found');
        }
      } else {
        setError(response.error || 'Failed to fetch station data');
        toast.error(response.error || 'Failed to fetch station data');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const parseTrainsData = (data: any): TrainAtStation[] => {
    // Handle different response structures
    if (Array.isArray(data)) {
      return data.map(train => ({
        trainNo: train.trainNo || train.train_no || train.number || 'N/A',
        trainName: train.trainName || train.train_name || train.name || 'Unknown',
        arrivalTime: train.arrivalTime || train.arrival || train.arr || '--',
        departureTime: train.departureTime || train.departure || train.dep || '--',
        platform: train.platform || train.pfNo || train.pf || 'TBA',
        delay: train.delay || train.delayMinutes || '0',
        status: train.status || 'Scheduled',
        ...train
      }));
    }
    
    if (data.trains && Array.isArray(data.trains)) {
      return data.trains.map((train: any) => ({
        trainNo: train.trainNo || train.train_no || train.number || 'N/A',
        trainName: train.trainName || train.train_name || train.name || 'Unknown',
        arrivalTime: train.arrivalTime || train.arrival || train.arr || '--',
        departureTime: train.departureTime || train.departure || train.dep || '--',
        platform: train.platform || train.pfNo || train.pf || 'TBA',
        delay: train.delay || train.delayMinutes || '0',
        status: train.status || 'Scheduled',
        ...train
      }));
    }
    
    if (data.data && Array.isArray(data.data)) {
      return data.data.map((train: any) => ({
        trainNo: train.trainNo || train.train_no || train.number || 'N/A',
        trainName: train.trainName || train.train_name || train.name || 'Unknown',
        arrivalTime: train.arrivalTime || train.arrival || train.arr || '--',
        departureTime: train.departureTime || train.departure || train.dep || '--',
        platform: train.platform || train.pfNo || train.pf || 'TBA',
        delay: train.delay || train.delayMinutes || '0',
        status: train.status || 'Scheduled',
        ...train
      }));
    }

    // If data is an object, try to extract trains
    const possibleTrains: TrainAtStation[] = [];
    
    Object.keys(data).forEach(key => {
      if (typeof data[key] === 'object' && data[key] !== null) {
        possibleTrains.push({
          trainNo: data[key].trainNo || data[key].train_no || data[key].number || 'N/A',
          trainName: data[key].trainName || data[key].train_name || data[key].name || 'Unknown',
          arrivalTime: data[key].arrivalTime || data[key].arrival || data[key].arr || '--',
          departureTime: data[key].departureTime || data[key].departure || data[key].dep || '--',
          platform: data[key].platform || data[key].pfNo || data[key].pf || 'TBA',
          delay: data[key].delay || data[key].delayMinutes || '0',
          status: data[key].status || 'Scheduled',
          ...data[key]
        });
      }
    });

    return possibleTrains;
  };

  const handleRefresh = () => {
    if (stationCode) {
      fetchStationData(stationCode);
    }
  };

  const handleNewSearch = () => {
    navigate('/live-station');
  };

  const getStatusColor = (status?: string) => {
    if (!status) return styles.scheduled;
    
    const s = status.toLowerCase();
    if (s.includes('arrived') || s.includes('running')) return styles.arrived;
    if (s.includes('delayed')) return styles.delayed;
    if (s.includes('cancelled')) return styles.cancelled;
    return styles.scheduled;
  };

  return (
    <div className={styles.liveStationResultPage}>
      <div className="container">
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FiNavigation className={styles.titleIcon} />
              Live at Station
            </h1>
            <div className={styles.headerActions}>
              <Button variant="outline" size="md" onClick={handleNewSearch}>
                <FiArrowLeft />
                New Search
              </Button>
              <Button variant="secondary" size="md" onClick={handleRefresh} isLoading={isLoading}>
                <FiRefreshCw />
                Refresh
              </Button>
            </div>
          </div>
          <p className={styles.subtitle}>Station Code: {stationCode}</p>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className={styles.resultsSection}>
            <Shimmer type="card" count={3} />
          </div>
        )}

        {/* Error */}
        {!isLoading && error && (
          <Card className={styles.errorCard}>
            <div className={styles.errorContent}>
              <FiAlertCircle className={styles.errorIcon} />
              <h3 className={styles.errorTitle}>Unable to Fetch Station Data</h3>
              <p className={styles.errorMessage}>{error}</p>
              <div className={styles.errorActions}>
                <Button variant="primary" onClick={handleRefresh}>
                  Try Again
                </Button>
                <Button variant="outline" onClick={handleNewSearch}>
                  New Search
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Results */}
        {!isLoading && !error && trains.length > 0 && (
          <div className={styles.resultsSection}>
            <div className={styles.resultsHeader}>
              <h3 className={styles.resultsTitle}>
                <FaTrain />
                {trains.length} Train{trains.length > 1 ? 's' : ''} at {stationCode}
              </h3>
              <p className={styles.resultsSubtitle}>Live train updates</p>
            </div>

            <div className={styles.trainsList}>
              {trains.map((train, index) => (
                <Card key={index} hover className={styles.trainCard}>
                  <div className={styles.trainHeader}>
                    <div className={styles.trainIdentity}>
                      <div className={styles.trainNumber}>
                        <FaTrain />
                        {train.trainNo}
                      </div>
                      <div className={styles.trainName}>{train.trainName}</div>
                    </div>
                    <div className={`${styles.statusBadge} ${getStatusColor(train.status)}`}>
                      {train.status || 'Scheduled'}
                    </div>
                  </div>

                  <div className={styles.trainDetails}>
                    <div className={styles.detailItem}>
                      <FiClock className={styles.detailIcon} />
                      <div className={styles.detailContent}>
                        <span className={styles.detailLabel}>Arrival</span>
                        <span className={styles.detailValue}>{train.arrivalTime}</span>
                      </div>
                    </div>

                    <div className={styles.detailItem}>
                      <FiClock className={styles.detailIcon} />
                      <div className={styles.detailContent}>
                        <span className={styles.detailLabel}>Departure</span>
                        <span className={styles.detailValue}>{train.departureTime}</span>
                      </div>
                    </div>

                    <div className={styles.detailItem}>
                      <FiMapPin className={styles.detailIcon} />
                      <div className={styles.detailContent}>
                        <span className={styles.detailLabel}>Platform</span>
                        <span className={styles.detailValue}>{train.platform}</span>
                      </div>
                    </div>

                    {train.delay && train.delay !== '0' && (
                      <div className={styles.detailItem}>
                        <FiAlertCircle className={styles.detailIcon} />
                        <div className={styles.detailContent}>
                          <span className={styles.detailLabel}>Delay</span>
                          <span className={styles.delayValue}>{train.delay} min</span>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* No Trains */}
        {!isLoading && !error && trains.length === 0 && stationData && (
          <Card className={styles.noTrainsCard}>
            <div className={styles.noTrainsContent}>
              <FaTrain className={styles.noTrainsIcon} />
              <h3>No Trains Currently</h3>
              <p>There are no trains at station {stationCode} right now</p>
              <div className={styles.debugInfo}>
                <details>
                  <summary>View Raw Response Data</summary>
                  <pre>{JSON.stringify(stationData, null, 2)}</pre>
                </details>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LiveStationResultPage;